import React, { useState, useEffect } from 'react';
import { getMyOrders, cancelOrder, confirmCompletion, reportNoShow } from '../../services/marketplaceOrderService';
import { submitRating } from '../../services/ratingService';
import { getSocket } from '../../utils/socketClient';
import { useToast } from '../../context/ToastContext';

const OrderCard = ({ order, onChanged }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [therapistScore, setTherapistScore] = useState(5);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const handleCancel = async () => {
    setBusy(true);
    await cancelOrder(order._id, 'Client requested cancellation');
    toast.info('Booking cancelled');
    onChanged();
    setBusy(false);
  };

  const handleNoShow = async () => {
    setBusy(true);
    await reportNoShow(order._id);
    toast.info('Reported as no-show');
    onChanged();
    setBusy(false);
  };

  const handleRateAndConfirm = async () => {
    setBusy(true);
    if (!ratingSubmitted) {
      await submitRating({ orderId: order._id, score: therapistScore });
      setRatingSubmitted(true);
    }
    await confirmCompletion(order._id);
    toast.success('Thanks for your feedback!');
    onChanged();
    setBusy(false);
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow space-y-2">
      <div className="flex justify-between">
        <span className="font-semibold">{order.massageType}</span>
        <span className="text-sm text-gray-500">{order.status.replace(/_/g, ' ')}</span>
      </div>
      <p className="text-sm text-gray-600">
        {new Date(order.scheduledDate).toLocaleDateString()} at {order.scheduledTime}
      </p>
      <p className="text-sm">Total: ₦{order.pricing.totalAmount.toLocaleString()}</p>

      {order.assignedTherapistId && (
        <p className="text-sm bg-gray-50 rounded-lg p-2">
          Therapist: {order.assignedTherapistId.firstName} {order.assignedTherapistId.lastName}
          {order.assignedTherapistId.phone && (
            <> — <a href={`tel:${order.assignedTherapistId.phone}`} className="text-blue-600 underline">
              {order.assignedTherapistId.phone}
            </a></>
          )}
        </p>
      )}

      {['pending_match', 'assigned'].includes(order.status) && (
        <div className="flex gap-2 pt-2">
          <button onClick={handleCancel} disabled={busy} className="text-sm bg-gray-200 px-3 py-1.5 rounded-lg">
            Cancel
          </button>
          {order.status === 'assigned' && (
            <button onClick={handleNoShow} disabled={busy} className="text-sm bg-red-100 text-red-700 px-3 py-1.5 rounded-lg">
              Report no-show
            </button>
          )}
        </div>
      )}

      {order.status === 'completed_pending_confirmation' && (
        <div className="pt-2 border-t space-y-2">
          <p className="text-sm font-medium">Rate your therapist</p>
          <select value={therapistScore} onChange={(e) => setTherapistScore(Number(e.target.value))}
            className="border rounded-lg p-2 text-sm">
            {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} star{n > 1 ? 's' : ''}</option>)}
          </select>
          <button onClick={handleRateAndConfirm} disabled={busy}
            className="block bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm">
            {ratingSubmitted ? 'Confirm completion' : 'Submit rating & confirm completion'}
          </button>
        </div>
      )}
    </div>
  );
};

const ClientOrders = () => {
  const toast = useToast();
  const [orders, setOrders] = useState([]);

  const refresh = () => getMyOrders().then(setOrders);

  useEffect(() => {
    refresh();

    const socket = getSocket();
    socket.connect();
    const onAssigned = () => {
      toast.info('A therapist has been assigned to your order!');
      refresh();
    };
    socket.on('order:assigned', onAssigned);
    return () => socket.off('order:assigned', onAssigned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      {orders.map(order => <OrderCard key={order._id} order={order} onChanged={refresh} />)}
      {orders.length === 0 && <p className="text-gray-500">No orders yet.</p>}
    </div>
  );
};

export default ClientOrders;
